import axios from 'axios';

// Configuration de base pour axios
const apiClient = axios.create({
  baseURL: 'https://api-gateway.zagogroupe.com/api/public',
  timeout: 30000, // 30 secondes
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour gérer les erreurs globalement
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Si erreur 401 (non autorisé) et pas déjà retenté
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      // Le token sera automatiquement rafraîchi par l'intercepteur de requête
      return apiClient(originalRequest);
    }

    // Gestion des erreurs réseau
    if (error.code === 'ECONNABORTED' || error.message === 'Network Error') {
      error.message = 'Erreur de connexion. Vérifiez votre connexion internet.';
    }

    // Gestion des erreurs HTTP
    if (error.response) {
      switch (error.response.status) {
        case 400:
          error.message = 'Requête invalide. Vérifiez les paramètres.';
          break;
        case 403:
          error.message = 'Accès refusé. Vous n\'avez pas les permissions nécessaires.';
          break;
        case 404:
          error.message = 'Ressource non trouvée.';
          break;
        case 500:
          error.message = 'Erreur serveur. Veuillez réessayer plus tard.';
          break;
        case 503:
          error.message = 'Service temporairement indisponible.';
          break;
        default:
          error.message = error.response.data?.message || 'Une erreur est survenue.';
      }
    }

    return Promise.reject(error);
  }
);

// Fonction utilitaire pour formater la date
export const formatFileDate = (dateString) => {
  if (!dateString) return '';
  const str = dateString.toString();
  if (str.length < 14) return '';
  
  return `${str.substring(6, 8)}/${str.substring(4, 6)}/${str.substring(0, 4)} à ${str.substring(8, 10)}:${str.substring(10, 12)}:${str.substring(12, 14)}`;
};

// Service pour les appels API stock
export const stockService = {
  /**
   * Récupère le stock pour un client donné
   * @param {string} shopId - ID du magasin/client
   * @param {string} token - Token d'authentification
   * @returns {Promise} Promise avec les données de stock
   */
  getStock: async (shopId, token) => {
    if (!shopId) {
      throw new Error('Shop ID est requis');
    }
    
    if (!token) {
      throw new Error('Token d\'authentification requis');
    }
    
    const response = await apiClient.get(`/client/${shopId}/stock`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },

  /**
   * Recherche des produits dans le stock
   * @param {string} shopId - ID du magasin/client
   * @param {string} query - Terme de recherche
   * @param {string} token - Token d'authentification
   * @returns {Promise} Promise avec les produits filtrés
   */
  searchProducts: async (shopId, query, token) => {
    if (!shopId) {
      throw new Error('Shop ID est requis');
    }
    
    if (!token) {
      throw new Error('Token d\'authentification requis');
    }
    
    if (!query || query.trim().length === 0) {
      return { StockLineList: [], Header: {} };
    }

    const stockData = await stockService.getStock(shopId, token);
    const upperQuery = query.toUpperCase().trim();
    
    const filteredProducts = stockData.StockLineList.filter((product) => {
      return (
        product.ProductSku?.includes(upperQuery) ||
        product.Gencod?.includes(query) ||
        product.ProductClientSku?.includes(upperQuery)
      );
    });

    return {
      ...stockData,
      StockLineList: filteredProducts,
    };
  },
};

export default apiClient;

